import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import AdminGate from "../components/admin/AdminGate";
import { adminFetch, adminUpload, fetchAuthedBlobUrl, forceReauth } from "../lib/admin";

const slugify = (s) => s
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * AdminContentEditPage — create/edit form for Ideas, Research, Projects.
 * Plain-text editing only: a blank line in a textarea starts a new
 * paragraph. No HTML is accepted or rendered.
 */

const TYPES = { ideas: "Idea", research: "Research", projects: "Project", memoire: "Memoire" };
const LANGS = ["en", "es", "nl", "fr"];
const PROJECT_STATUSES = ["in progress", "ongoing", "concluded"];

const COMMON_FIELDS = [
    { name: "title", label: "Title", kind: "text", required: true },
    { name: "slug", label: "Slug (public URL)", kind: "text", hint: "Generated from the title — you can edit it" },
    { name: "subtitle", label: "Subtitle / short description", kind: "textarea", rows: 2 },
    { name: "language", label: "Language", kind: "select", options: LANGS },
    { name: "author", label: "Author", kind: "text" },
    { name: "publication_date", label: "Publication date", kind: "text", hint: "e.g. October 2026" },
    { name: "category", label: "Category", kind: "text" },
];

const TYPE_FIELDS = {
    ideas: [
        { name: "body", label: "Body", kind: "textarea", rows: 14, hint: "Plain text. A blank line starts a new paragraph." },
        { name: "tags", label: "Tags", kind: "csv" },
        { name: "number", label: "Editorial ordinal", kind: "text", hint: "e.g. N° 008 (optional)" },
        { name: "reading_time", label: "Reading time", kind: "text", hint: "e.g. 6 min read (optional)" },
        { name: "cover_image", label: "Cover image", kind: "image" },
    ],
    research: [
        { name: "abstract", label: "Abstract", kind: "textarea", rows: 4 },
        { name: "executive_summary", label: "Executive summary", kind: "textarea", rows: 6 },
        { name: "methodology", label: "Methodology", kind: "textarea", rows: 6 },
        { name: "findings", label: "Main findings", kind: "textarea", rows: 6 },
        { name: "references", label: "References", kind: "textarea", rows: 4 },
        { name: "body", label: "Full text (optional)", kind: "textarea", rows: 10, hint: "Plain text. A blank line starts a new paragraph." },
        { name: "keywords", label: "Keywords", kind: "csv" },
        { name: "number", label: "Editorial ordinal", kind: "text", hint: "e.g. N° 008 (optional)" },
        { name: "reading_time", label: "Reading time", kind: "text", hint: "e.g. 12 min read (optional)" },
        { name: "cover_image", label: "Cover image", kind: "image" },
        { name: "pdf_file", label: "Research paper (PDF, max 25 MB)", kind: "pdf" },
        { name: "external_links", label: "External links", kind: "links" },
    ],
    memoire: [
        { name: "body", label: "Main content", kind: "textarea", rows: 14, hint: "Plain text. A blank line starts a new paragraph." },
        { name: "context", label: "Context — what was happening at the time? (optional)", kind: "textarea", rows: 4 },
        { name: "experience", label: "Experience — what happened? (optional)", kind: "textarea", rows: 5 },
        { name: "reflection", label: "Reflection — what did it make you think about? (optional)", kind: "textarea", rows: 5 },
        { name: "lesson", label: "Lesson — what was learned? (optional)", kind: "textarea", rows: 4 },
        { name: "growth", label: "Growth — how did it change you? (optional)", kind: "textarea", rows: 4 },
        { name: "note_to_reader", label: "Note to reader — closing takeaway (optional)", kind: "textarea", rows: 3 },
        { name: "tags", label: "Tags", kind: "csv" },
        { name: "cover_image", label: "Cover image", kind: "image" },
    ],
    projects: [
        { name: "period", label: "Date / period", kind: "text", hint: "e.g. 2024–2026" },
        { name: "location", label: "Location", kind: "text" },
        { name: "project_status", label: "Project status", kind: "select", options: PROJECT_STATUSES },
        { name: "problem", label: "Problem / context", kind: "textarea", rows: 5 },
        { name: "objectives", label: "Objectives", kind: "textarea", rows: 5 },
        { name: "approach", label: "Approach", kind: "textarea", rows: 5 },
        { name: "implementation", label: "Implementation", kind: "textarea", rows: 5 },
        { name: "results", label: "Results", kind: "textarea", rows: 5 },
        { name: "impact", label: "Impact", kind: "textarea", rows: 4 },
        { name: "lessons", label: "Lessons learned", kind: "textarea", rows: 4 },
        { name: "cover_image", label: "Cover image", kind: "image" },
        { name: "gallery_images", label: "Additional images", kind: "images" },
        { name: "documents", label: "Supporting documents (PDF)", kind: "docs" },
        { name: "external_links", label: "External links", kind: "links" },
    ],
};

const SEO_FIELDS = [
    { name: "seo_title", label: "SEO title", kind: "text" },
    { name: "seo_description", label: "SEO description", kind: "textarea", rows: 2 },
];

const EMPTY = {
    title: "", slug: "", subtitle: "", language: "en", author: "", publication_date: "",
    category: "", tags: [], keywords: [], number: "", reading_time: "",
    body: "", abstract: "", executive_summary: "", methodology: "", findings: "", references: "",
    problem: "", objectives: "", approach: "", implementation: "", results: "", impact: "", lessons: "",
    context: "", experience: "", reflection: "", lesson: "", growth: "", note_to_reader: "",
    period: "", location: "", project_status: "",
    cover_image: null, gallery_images: [], pdf_file: null, documents: [], external_links: [],
    seo_title: "", seo_description: "", translation: null, related: [],
};

/* ---------- small field components ---------- */

const labelCls = "font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] block mb-2";
const inputCls = "w-full bg-transparent border border-[color:var(--line-strong)] px-4 py-3 text-[15px] text-[color:var(--navy)] placeholder:text-[color:var(--warm-gray-2)]/70 focus:outline-none focus:border-[color:var(--gold)] transition-colors";

function Field({ def, value, onChange }) {
    const { name, label, kind, hint } = def;
    return (
        <div data-testid={`field-${name}`}>
            <label htmlFor={`f-${name}`} className={labelCls}>{label}</label>
            {kind === "textarea" ? (
                <textarea id={`f-${name}`} rows={def.rows || 4} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} leading-[1.65]`} />
            ) : kind === "select" ? (
                <select id={`f-${name}`} value={value || def.options[0]} onChange={(e) => onChange(e.target.value)} className={inputCls}>
                    {def.options.map((o) => <option key={o} value={o}>{o.toUpperCase()}</option>)}
                </select>
            ) : kind === "csv" ? (
                <input
                    id={`f-${name}`}
                    type="text"
                    value={(value || []).join(", ")}
                    onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                    placeholder="Comma separated"
                    className={inputCls}
                />
            ) : (
                <input id={`f-${name}`} type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
            )}
            {hint && <p className="mt-1 text-[12px] text-[color:var(--warm-gray-2)]">{hint}</p>}
        </div>
    );
}

function AdminImage({ path }) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        let alive = true;
        let obj = null;
        fetchAuthedBlobUrl(path).then((u) => { if (alive) { obj = u; setUrl(u); } });
        return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
    }, [path]);
    if (!url) return <div className="h-24 w-40 bg-[color:var(--line)]" aria-hidden />;
    return <img src={url} alt="" className="h-24 w-auto border border-[color:var(--line)]" />;
}

function UploadButton({ kind, testId, busy, onUploaded }) {
    return (
        <label className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase cursor-pointer">
            <input
                type="file"
                data-testid={testId}
                accept={kind === "image" ? "image/*" : "application/pdf"}
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) onUploaded(f);
                    e.target.value = "";
                }}
            />
            {busy ? "Uploading…" : "Upload"}
        </label>
    );
}

/* ---------- main form ---------- */

function Editor({ ctype, id }) {
    const isNew = !id;
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("draft");
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const [reloadKey, setReloadKey] = useState(0);
    const [linked, setLinked] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [linkTarget, setLinkTarget] = useState("");
    const [relCandidates, setRelCandidates] = useState([]);
    const [relTarget, setRelTarget] = useState("");
    const [typeSel, setTypeSel] = useState(ctype);
    const [confirmTypeSave, setConfirmTypeSave] = useState(null); // status pending type-change confirmation
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => { setTypeSel(ctype); }, [ctype]);

    // Memoire: candidates for cross-type "related content" links.
    useEffect(() => {
        if (typeSel !== "memoire") return;
        let cancelled = false;
        Promise.all(
            Object.keys(TYPES).map((t) =>
                adminFetch(`/admin/content/${t}`)
                    .then((r) => (r.ok ? r.json() : []))
                    .then((list) => list.map((x) => ({ ...x, _type: t })))
                    .catch(() => [])
            )
        ).then((lists) => {
            if (!cancelled) setRelCandidates(lists.flat().filter((x) => x.id !== id));
        });
        return () => { cancelled = true; };
    }, [typeSel, id]);

    useEffect(() => {
        if (isNew) return;
        let cancelled = false;
        adminFetch(`/admin/content/${ctype}/${id}`)
            .then(async (resp) => {
                if (cancelled) return null;
                if (resp.status === 401) { forceReauth(); return null; }
                if (!resp.ok) { setError("Could not load this item."); setLoading(false); return null; }
                const doc = await resp.json();
                setForm({ ...EMPTY, ...doc });
                setStatus(doc.status || "draft");
                setLinked(doc.linked_versions || []);
                setLoading(false);
                return doc;
            })
            .then((doc) => {
                if (!doc || cancelled) return;
                adminFetch(`/admin/content/${ctype}`)
                    .then((r) => (r.ok ? r.json() : []))
                    .then((list) => {
                        if (cancelled) return;
                        setCandidates(list.filter((x) =>
                            x.id !== id &&
                            (x.language || "en") !== (doc.language || "en") &&
                            !(x.translation_group && doc.translation_group && x.translation_group === doc.translation_group)
                        ));
                    })
                    .catch(() => {});
            })
            .catch(() => { if (!cancelled) { setError("Network error."); setLoading(false); } });
        return () => { cancelled = true; };
    }, [ctype, id, isNew, reloadKey]);

    const doLink = async () => {
        if (!linkTarget) return;
        setError(null);
        try {
            const resp = await adminFetch(`/admin/content/${ctype}/${id}/link`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ other_id: linkTarget }),
            });
            const data = await resp.json().catch(() => ({}));
            if (resp.status === 401) { forceReauth(); return; }
            if (!resp.ok) { setError(data.detail || "Could not link these publications."); return; }
            setLinkTarget("");
            setReloadKey((k) => k + 1);
        } catch (_e) { setError("Network error — could not link."); }
    };

    const doUnlink = async () => {
        setError(null);
        try {
            const resp = await adminFetch(`/admin/content/${ctype}/${id}/unlink`, { method: "POST" });
            if (resp.status === 401) { forceReauth(); return; }
            if (resp.ok) setReloadKey((k) => k + 1);
        } catch (_e) { setError("Network error — could not unlink."); }
    };

    const slugTouched = useRef(false);
    const set = useCallback((name) => (value) => setForm((f) => {
        if (name === "slug") slugTouched.current = true;
        if (name === "title" && isNew && !slugTouched.current) {
            return { ...f, title: value, slug: slugify(value) };
        }
        return { ...f, [name]: value };
    }), [isNew]);
    const setTr = useCallback((name) => (value) => setForm((f) => ({ ...f, translation: { ...(f.translation || {}), [name]: value } })), []);

    const doUpload = async (kind, file, apply) => {
        setUploading(true);
        setError(null);
        try {
            const resp = await adminUpload(kind, file);
            const data = await resp.json().catch(() => ({}));
            if (resp.status === 401) { forceReauth(); return; }
            if (!resp.ok) { setError(data.detail || `Upload failed (HTTP ${resp.status}).`); return; }
            apply(data);
        } catch (_e) {
            setError("Upload failed — network error.");
        } finally {
            setUploading(false);
        }
    };

    const save = async (nextStatus) => {
        if (!form.title.trim()) { setError("A title is required."); return; }
        if (!isNew && typeSel !== ctype) { setConfirmTypeSave(nextStatus); return; }
        setSaving(true);
        setError(null);
        const payload = { ...form, status: nextStatus };
        if (!payload.slug) delete payload.slug;
        try {
            const resp = await adminFetch(
                isNew ? `/admin/content/${typeSel}` : `/admin/content/${ctype}/${id}`,
                {
                    method: isNew ? "POST" : "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            const data = await resp.json().catch(() => ({}));
            if (resp.status === 401) { forceReauth(); return; }
            if (!resp.ok) { setError(data.detail || `Save failed (HTTP ${resp.status}).`); return; }
            navigate(`/admin?filter=${typeSel}`);
        } catch (_e) {
            setError("Save failed — network error.");
        } finally {
            setSaving(false);
        }
    };

    /* Change content type of an existing entry: save current edits first,
     * then move the SAME entry (same id) to the target collection. */
    const doChangeType = async () => {
        const nextStatus = confirmTypeSave || status;
        setConfirmTypeSave(null);
        setSaving(true);
        setError(null);
        try {
            const payload = { ...form, status: nextStatus };
            const resp = await adminFetch(`/admin/content/${ctype}/${id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await resp.json().catch(() => ({}));
            if (resp.status === 401) { forceReauth(); return; }
            if (!resp.ok) { setError(data.detail || `Save failed (HTTP ${resp.status}).`); return; }
            const mv = await adminFetch(`/admin/content/${ctype}/${id}/change-type`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ target: typeSel }),
            });
            const mvData = await mv.json().catch(() => ({}));
            if (mv.status === 401) { forceReauth(); return; }
            if (!mv.ok) { setError(mvData.detail || `Type change failed (HTTP ${mv.status}).`); return; }
            navigate(`/admin?filter=${typeSel}`);
        } catch (_e) {
            setError("Type change failed — network error.");
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        setSaving(true);
        try {
            const resp = await adminFetch(`/admin/content/${ctype}/${id}`, { method: "DELETE" });
            if (resp.status === 401) { forceReauth(); return; }
            if (resp.ok) { navigate(`/admin?filter=${ctype}`); return; }
            setError("Delete failed.");
        } catch (_e) {
            setError("Delete failed — network error.");
        } finally {
            setSaving(false);
            setConfirmDelete(false);
        }
    };

    if (loading) {
        return <p className="mt-12 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)]">Loading…</p>;
    }

    const fields = [...COMMON_FIELDS, ...TYPE_FIELDS[typeSel], ...SEO_FIELDS];

    return (
        <form
            data-testid="content-form"
            className="mt-10 border-t border-[color:var(--line-strong)] pt-8 space-y-8 max-w-[760px]"
            onSubmit={(e) => { e.preventDefault(); save(status); }}
        >
            {/* Content type — editable after creation; the form adapts */}
            <div data-testid="field-content-type">
                <label htmlFor="f-content-type" className={labelCls}>Content type</label>
                <select
                    id="f-content-type"
                    data-testid="content-type-select"
                    value={typeSel}
                    onChange={(e) => setTypeSel(e.target.value)}
                    className={inputCls}
                >
                    {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v.toUpperCase()}</option>)}
                </select>
                {!isNew && typeSel !== ctype && (
                    <p data-testid="type-change-notice" className="mt-2 text-[12px] text-[color:var(--warm-gray-2)]">
                        Will change from {TYPES[ctype].toUpperCase()} to {TYPES[typeSel].toUpperCase()} when you save.
                        All content, dates, status and slug are preserved.
                    </p>
                )}
            </div>

            {error && <p data-testid="content-error" role="alert" className="text-[13px] text-[#9A3B26]">{error}</p>}

            {fields.map((def) => {
                if (def.kind === "image") {
                    return (
                        <div key={def.name} data-testid={`field-${def.name}`}>
                            <span className={labelCls}>{def.label}</span>
                            <div className="flex items-center gap-5">
                                {form[def.name] && <AdminImage path={form[def.name]} />}
                                <UploadButton
                                    kind="image"
                                    testId={`upload-${def.name}`}
                                    busy={uploading}
                                    onUploaded={(f) => doUpload("image", f, (d) => set(def.name)(d.path))}
                                />
                                {form[def.name] && (
                                    <button type="button" onClick={() => set(def.name)(null)} className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                }
                if (def.kind === "images") {
                    return (
                        <div key={def.name} data-testid={`field-${def.name}`}>
                            <span className={labelCls}>{def.label}</span>
                            <div className="flex flex-wrap items-center gap-5">
                                {(form[def.name] || []).map((p, i) => (
                                    <div key={p} className="flex flex-col items-start gap-2">
                                        <AdminImage path={p} />
                                        <button
                                            type="button"
                                            onClick={() => set(def.name)((form[def.name] || []).filter((_, j) => j !== i))}
                                            className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                                <UploadButton
                                    kind="image"
                                    testId={`upload-${def.name}`}
                                    busy={uploading}
                                    onUploaded={(f) => doUpload("image", f, (d) => set(def.name)([...(form[def.name] || []), d.path]))}
                                />
                            </div>
                        </div>
                    );
                }
                if (def.kind === "pdf") {
                    return (
                        <div key={def.name} data-testid={`field-${def.name}`}>
                            <span className={labelCls}>{def.label}</span>
                            <div className="flex items-center gap-5">
                                {form[def.name] && (
                                    <span className="font-mono text-[11px] tracking-[0.12em] text-[color:var(--navy)]/80">
                                        PDF attached
                                    </span>
                                )}
                                <UploadButton
                                    kind="pdf"
                                    testId={`upload-${def.name}`}
                                    busy={uploading}
                                    onUploaded={(f) => doUpload("pdf", f, (d) => set(def.name)(d.path))}
                                />
                                {form[def.name] && (
                                    <button type="button" onClick={() => set(def.name)(null)} className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                }
                if (def.kind === "docs") {
                    return (
                        <div key={def.name} data-testid={`field-${def.name}`}>
                            <span className={labelCls}>{def.label}</span>
                            <ul className="space-y-2 mb-3">
                                {(form[def.name] || []).map((d, i) => (
                                    <li key={d.path} className="flex items-center gap-4 text-[13px] text-[color:var(--navy)]/85">
                                        <span className="truncate max-w-[320px]">{d.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => set(def.name)((form[def.name] || []).filter((_, j) => j !== i))}
                                            className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                                        >
                                            Remove
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <UploadButton
                                kind="pdf"
                                testId={`upload-${def.name}`}
                                busy={uploading}
                                onUploaded={(f) => doUpload("pdf", f, (d) => set(def.name)([...(form[def.name] || []), { path: d.path, name: d.name }]))}
                            />
                        </div>
                    );
                }
                if (def.kind === "links") {
                    const links = form[def.name] || [];
                    return (
                        <div key={def.name} data-testid={`field-${def.name}`}>
                            <span className={labelCls}>{def.label}</span>
                            <div className="space-y-3">
                                {links.map((l, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            value={l.label}
                                            placeholder="Label"
                                            onChange={(e) => set(def.name)(links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                                            className={`${inputCls} max-w-[220px]`}
                                        />
                                        <input
                                            type="url"
                                            value={l.url}
                                            placeholder="https://…"
                                            onChange={(e) => set(def.name)(links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                                            className={inputCls}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => set(def.name)(links.filter((_, j) => j !== i))}
                                            className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)] shrink-0"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    data-testid={`add-${def.name}`}
                                    onClick={() => set(def.name)([...links, { label: "", url: "" }])}
                                    className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                                >
                                    + Add link
                                </button>
                            </div>
                        </div>
                    );
                }
                return <Field key={def.name} def={def} value={form[def.name]} onChange={set(def.name)} />;
            })}

            {/* Translation slot (present on migrated EN/NL essays) */}
            {form.translation && (
                <fieldset data-testid="field-translation" className="border border-[color:var(--line)] p-6 space-y-6">
                    <legend className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] px-2">
                        Existing translation · {(form.translation.language || "").toUpperCase()}
                    </legend>
                    <Field def={{ name: "tr-title", label: "Translated title", kind: "text" }} value={form.translation.title} onChange={setTr("title")} />
                    <Field def={{ name: "tr-subtitle", label: "Translated subtitle", kind: "textarea", rows: 2 }} value={form.translation.subtitle} onChange={setTr("subtitle")} />
                    <Field def={{ name: "tr-body", label: "Translated body", kind: "textarea", rows: 10, hint: "Plain text. A blank line starts a new paragraph." }} value={form.translation.body} onChange={setTr("body")} />
                </fieldset>
            )}

            {/* Localized versions — link manually-created translations */}
            {!isNew && (
                <fieldset data-testid="field-localized-versions" className="border border-[color:var(--line)] p-6 space-y-5">
                    <legend className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] px-2">
                        Localized versions
                    </legend>
                    {linked.length === 0 ? (
                        <p className="text-[13px] text-[color:var(--warm-gray-2)]">No linked versions yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {linked.map((v) => (
                                <li key={v.id} data-testid="linked-version-row" className="flex items-center gap-4 text-[13px] text-[color:var(--navy)]/85">
                                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase border border-[color:var(--line)] px-1.5 py-0.5 shrink-0">
                                        {(v.language || "en").toUpperCase()}
                                    </span>
                                    <span className="truncate max-w-[320px]">{v.title}</span>
                                    <span className="font-mono text-[10px] uppercase text-[color:var(--warm-gray-2)]">{v.status}</span>
                                    <button
                                        type="button"
                                        data-testid="unlink-version-button"
                                        onClick={() => doUnlink(v.id)}
                                        className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                                    >
                                        Unlink
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {candidates.length > 0 && (
                        <div className="flex flex-wrap items-center gap-3">
                            <select
                                value={linkTarget}
                                onChange={(e) => setLinkTarget(e.target.value)}
                                data-testid="link-version-select"
                                className={`${inputCls} max-w-[420px]`}
                            >
                                <option value="">Select a publication to link…</option>
                                {candidates.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {(c.language || "en").toUpperCase()} · {c.title} · {c.status}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                data-testid="link-version-button"
                                onClick={doLink}
                                disabled={!linkTarget}
                                className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                            >
                                Link version
                            </button>
                        </div>
                    )}
                    <p className="text-[12px] leading-[1.6] text-[color:var(--warm-gray-2)]">
                        Each language version is a separate publication you create yourself. Only
                        publications in a different language can be linked. Nothing is translated
                        automatically.
                    </p>
                </fieldset>
            )}

            {/* Related content — optional cross-type relationships (Memoire) */}
            {typeSel === "memoire" && (
                <fieldset data-testid="field-related" className="border border-[color:var(--line)] p-6 space-y-5">
                    <legend className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--warm-gray-2)] px-2">
                        Related content (optional)
                    </legend>
                    {(form.related || []).length === 0 ? (
                        <p className="text-[13px] text-[color:var(--warm-gray-2)]">No related entries yet.</p>
                    ) : (
                        <ul className="space-y-2">
                            {(form.related || []).map((r) => {
                                const target = relCandidates.find((c) => c._type === r.type && c.id === r.id);
                                return (
                                    <li key={`${r.type}-${r.id}`} data-testid="related-row" className="flex items-center gap-4 text-[13px] text-[color:var(--navy)]/85">
                                        <span className="font-mono text-[10px] tracking-[0.18em] uppercase border border-[color:var(--line)] px-1.5 py-0.5 shrink-0">
                                            {TYPES[r.type] || r.type}
                                        </span>
                                        <span className="truncate max-w-[320px]">{target ? target.title : r.id}</span>
                                        <button
                                            type="button"
                                            data-testid="related-remove-button"
                                            onClick={() => set("related")((form.related || []).filter((x) => !(x.type === r.type && x.id === r.id)))}
                                            className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                                        >
                                            Remove
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {relCandidates.filter((c) => !(form.related || []).some((r) => r.type === c._type && r.id === c.id)).length > 0 && (
                        <div className="flex flex-wrap items-center gap-3">
                            <select
                                value={relTarget}
                                onChange={(e) => setRelTarget(e.target.value)}
                                data-testid="related-select"
                                className={`${inputCls} max-w-[420px]`}
                            >
                                <option value="">Select an entry to connect…</option>
                                {relCandidates
                                    .filter((c) => !(form.related || []).some((r) => r.type === c._type && r.id === c.id))
                                    .map((c) => (
                                        <option key={`${c._type}-${c.id}`} value={`${c._type}:${c.id}`}>
                                            {`${TYPES[c._type]} · ${c.title} · ${c.status}`}
                                        </option>
                                    ))}
                            </select>
                            <button
                                type="button"
                                data-testid="related-add-button"
                                disabled={!relTarget}
                                onClick={() => {
                                    const [type, rid] = relTarget.split(":");
                                    set("related")([...(form.related || []), { type, id: rid }]);
                                    setRelTarget("");
                                }}
                                className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                            >
                                Connect
                            </button>
                        </div>
                    )}
                    <p className="text-[12px] leading-[1.6] text-[color:var(--warm-gray-2)]">
                        Optional links to Ideas, Research, Projects or other Memoire entries — for example,
                        the idea that emerged from this experience. Only published entries are shown publicly.
                    </p>
                </fieldset>
            )}

            <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-[color:var(--line)]">
                <button type="button" data-testid="content-save-draft" disabled={saving} onClick={() => save("draft")} className="btn-editorial btn-ghost">
                    {saving ? "Saving…" : status === "published" ? "Unpublish & save as draft" : "Save as draft"}
                </button>
                <button type="button" data-testid="content-publish" disabled={saving} onClick={() => save("published")} className="btn-editorial">
                    {saving ? "Saving…" : "Publish"}
                    <span aria-hidden className="w-4 h-px bg-current opacity-70" />
                </button>
                <Link to={`/admin?filter=${ctype}`} data-testid="content-cancel" className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase">
                    Cancel
                </Link>
                {!isNew && (
                    confirmDelete ? (
                        <>
                            <button
                                type="button"
                                data-testid="content-confirm-delete"
                                disabled={saving}
                                onClick={doDelete}
                                className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[#9A3B26]"
                            >
                                Confirm delete
                            </button>
                            <button
                                type="button"
                                data-testid="content-cancel-delete"
                                onClick={() => setConfirmDelete(false)}
                                className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                            >
                                Keep
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            data-testid="content-delete-button"
                            onClick={() => setConfirmDelete(true)}
                            className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                        >
                            Delete
                        </button>
                    )
                )}
                {!isNew && form.preview_token && (
                    <a
                        href={`/preview/${form.preview_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="content-preview-link"
                        className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase"
                    >
                        Preview
                    </a>
                )}
            </div>

            {/* Type-change confirmation — one simple dialog */}
            {confirmTypeSave !== null && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--navy)]/30 px-6"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Confirm content type change"
                    data-testid="confirm-type-change"
                    onClick={() => setConfirmTypeSave(null)}
                >
                    <div
                        className="bg-[color:var(--ivory)] border border-[color:var(--line-strong)] max-w-[480px] w-full p-8"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="font-display font-light text-[24px] leading-[1.15] text-[color:var(--navy)]">
                            Change content type from {TYPES[ctype].toUpperCase()} to {TYPES[typeSel].toUpperCase()}?
                        </h2>
                        <p className="mt-4 text-[13px] leading-[1.65] text-[color:var(--warm-gray-2)]">
                            The same entry is kept — title, content, images, tags, dates, status and slug
                            are preserved. Its public address moves to /{typeSel}/{form.slug || "…"}.
                        </p>
                        <div className="mt-8 flex items-center gap-5">
                            <button
                                type="button"
                                data-testid="type-change-confirm"
                                disabled={saving}
                                onClick={doChangeType}
                                className="btn-editorial"
                            >
                                {saving ? "Changing…" : "Change type"}
                                <span aria-hidden className="w-4 h-px bg-current opacity-70" />
                            </button>
                            <button
                                type="button"
                                data-testid="type-change-cancel"
                                onClick={() => setConfirmTypeSave(null)}
                                className="link-editorial font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
}

export default function AdminContentEditPage() {
    const { ctype, id } = useParams();
    if (!TYPES[ctype]) return <Navigate to="/admin" replace />;
    const label = TYPES[ctype];
    return (
        <AdminGate title={id ? `Edit ${label.toLowerCase()}.` : `New ${label.toLowerCase()}.`}>
            <Editor ctype={ctype} id={id} />
        </AdminGate>
    );
}
